<template>
  <div
    v-if="!focusMode"
    ref="main"
    class="relative box-border flex h-full min-h-screen overflow-hidden bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
  >
    <div class="absolute w-screen h-screen" id="animbg"></div>
    <Transition
      mode="out-in"
      enter-active-class="ease-out duration-150"
      enter-from-class="opacity-0 -translate-x-8"
      leave-active-class="ease-in duration-150"
      leave-to-class="opacity-0 -translate-x-8"
    >
      <Sidebar
        v-if="!hideNavigation"
        ref="sidebarRef"
        :minimized="minimized"
        class="fixed z-[11] auto-cols-min transition-all"
        :class="{ 'shadow-2xl md:shadow-none': !sidebar.offScreen }"
        :max-width="(isMobile && !sidebar.offScreen ? '100%' : (sidebarMaxWidth || 260))"
        :style="{ left: isMobile && sidebar.offScreen ? `-${sidebarMaxWidth || 260}px` : 0 }"
        @on-minimize="onMinimize"
      />
    </Transition>

    <div
      class="relative box-border min-h-full w-full max-w-[100vw] transition-[padding-left] duration-200"
      :style="{
        paddingLeft: (isMobile || hideNavigation) ? 0 : `${!sidebar.minimize ? (sidebarMaxWidth || 260) : 80}px`,
      }"
    >
      <!-- Bottom Bar -->
      <div
        class="fixed bottom-0 right-0 z-20 bg-neutral-50 dark:bg-neutral-800 w-full px-4 py-2 block md:hidden"
        :style="{ paddingLeft: isMobile ? '16px' : `${!sidebar.minimize ? (sidebarMaxWidth || 260) : 80}px` }"
      >
        <UiButton
          type="ghost"
          class="p-2"
          icon="menu-2"
          @click.stop.prevent="() => sidebar.offScreen = !sidebar.offScreen"
        />
      </div>

      <!-- Modo Cliente -->
      <div v-if="useAuthStore()?.clientMode.contract">
        <div class="box-border w-full duration-200 bg-amber-200 dark:bg-amber-800 text-neutral-700 dark:text-amber-200 py-2 px-4 flex items-center justify-between">
          <div>
            <span class="text-amber-700 dark:text-amber-300">
              <UiIcon icon="alert-triangle" size="18" :stroke-width="2" /></span> Modo cliente: <span class="font-sm font-semibold">{{ useAuthStore()?.clientMode.contract }}
            </span>
          </div>

          <UiButton type="info" class="p-2 flex items-center" @click="() => exitClientMode()">
            <UiIcon icon="door-exit" size="16" :stroke-width="2" class="mr-1 -scale-x-100" /> {{ $t('button.exitContract') }}
          </UiButton>
        </div>
      </div>

      <div id="main-content" class="relative box-border h-full w-full p-6">
        <RouterView v-slot="{ Component }">
          <Transition
            mode="out-in"
            enter-active-class="ease-out duration-150"
            enter-from-class="opacity-20 -translate-y-8"
            leave-active-class="ease-in duration-150"
            leave-to-class="opacity-20 -translate-y-8"
          >
            <Component :is="Component" class="mx-auto max-w-[1820px]"/>
          </Transition>
        </RouterView>
      </div>
    </div>

    <ModalPlayer
      v-if="systemStore.playing.src"
      ref="modalPlayer"
      :mediaId="systemStore.playing?.mediaId"
      :src="systemStore.playing.src"
      :type="systemStore.playing?.type"
      :progress="systemStore.playing?.progress ?? 0"
      :track-progress="systemStore.playing?.trackProgress"
      :title="systemStore.playing.title"
      :obj-data="systemStore.playing.objData"
      :allow-download="systemStore.playing.allowDownload"
      :access-detail="systemStore.playing.accessDetail"
      :ignore-filter-section="systemStore.playing.ignoreFilterSection"
      @on-close="() => stopPlaying()"
      @on-register-progress="setLocalProgress"
    />

    <ItemDetail />

    <TermsAgreementModal v-if="systemStore && systemStore?.viewTermsOfUse.id" />
    <GameBar />
    <ViewCommunication ref="communicationsRef" />
    <WellBeingIndicator ref="wellBeingIndicator" />

    <template v-if="isBetaEnv">
      <AiChat />
    </template>

    <GameExtras />
    <TrackSale v-if="isProd" />
  </div>
  <div v-else class="relative box-border h-full min-h-screen overflow-hidden bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300">
    <FocusTopBar :style="{ minHeight: `70px` }" />

    <RouterView v-slot="{ Component }">
      <Component :is="Component" class="h-full p-4"/>
    </RouterView>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeMount, ref, watch, nextTick, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useRouteHash } from '@vueuse/router'
import { useStorage, useWindowSize, onClickOutside } from '@vueuse/core'
import { 
  useSystemStore, 
  useAuthStore, 
  useComunicacoesStore, 
  useContratosStore, 
  useSecoesStore, 
  useIndicadorDeBemEstarRespostaStore,
  useCustomizationBackgroundBannerStore
} from '@/stores'
import webStorage from '@/common/webStorage'
import { i18N, dialog } from '@/common/util'
import { useSystem } from '@/composables/useSystem.js'

import { useThemeSync } from '@/composables/useThemeSync'
import { useSidebarMenu } from '@/composables/useSidebarMenu'

import UiButton from '@/components/ui/button.vue'
import UiIcon from '@/components/ui/icon.vue'

import GameBar from '@/components/gamification/sidebar.vue'
import ModalPlayer from '@/components/player/modal.vue'
import ItemDetail from '@/components/home/itemDetail.vue'
import Sidebar from './components/sidebar.vue'
import WellBeingIndicator from '@/components/modais/bemEstar/modalAnswerIndicator.vue'

import GameExtras from '@/components/gamificationV2/gameExtras.vue'

const AiChat = defineAsyncComponent(() => import('@/components/ai/chat.vue'))

const FocusTopBar = defineAsyncComponent(() => import('../focus/components/topbar.vue'))
const TrackSale = defineAsyncComponent(() => import('@/components/trackSale.vue'))

const { width } = useWindowSize()

const TermsAgreementModal = defineAsyncComponent(() => import('@/components/modais/usuario/modalAceiteTermoDeUso.vue'))
const ViewCommunication = defineAsyncComponent(() => import('@/components/modais/comunicacao/modalViewCommunication.vue'))

const focusMode = ref(false)

const { isBetaEnv, isProd } = useSystem()
const systemStore = useSystemStore()
const customizationStore = useCustomizationBackgroundBannerStore()
const indicadorRespostaStore = useIndicadorDeBemEstarRespostaStore()

useThemeSync()

const main = ref<HTMLDivElement | null>()
const sidebarRef = ref<HTMLDivElement | null>()
const modalPlayer = ref()

const route = useRoute()
const router = useRouter()
const hash = useRouteHash()

const sidebar = useStorage<any>('sideBar', {
  minimize: false,
  offScreen: false
})

const hideNavigation = computed(() => !useSidebarMenu().visible.value || route.meta?.hideNavigation)

const communicationsRef = ref()
const wellBeingIndicator = ref()

const sidebarMaxWidth = computed(() => Number(route.meta?.sidebarMaxWidth) ?? 260)
const isMobile = computed(() => width.value < 768)
const minimized = computed(() => isMobile.value ? false : (sidebar.value.minimize))

watch(
  () => systemStore.playing.src,
  (nVal) => {
    if (nVal) nextTick(() => modalPlayer.value?.open())
  }
)

watch(
  () => useComunicacoesStore().showCommunications,
  () => useComunicacoesStore().showCommunications && communicationsRef.value?.open()
)

watch(
  () => indicadorRespostaStore.showIndicator,
  newValue => {
    if (newValue){
      nextTick(() => {
        wellBeingIndicator.value?.open()
      })
    }
  },{ immediate: true }
)

const exitClientMode = async () => {
  const dialogResult = new Promise((resolve) => {
    dialog.open({
      alt: true,
      title: i18N('dialogs.setClientMode.exit'),
      message: i18N('dialogs.setClientMode.msgExit'),
      complete: () => {
        resolve(true)
      }
    })
  })

  if (!await dialogResult) {
    return
  }

  try {
    useAuthStore()?.exitClientMode()
    useAuthStore().getUserInfo()

    nextTick(async () => {
      systemStore.loadParams()
      await useContratosStore()?.getIsDistribuidor()
      customizationStore.loadExistingData()

      if (route.path === '/conta/contrato') {
        router.push('/')
      } else {
        router.push('/conta/contrato')
      }
    })
  } catch (error) {
    //
  }
}

onClickOutside(sidebarRef, () => {
  if (width.value <= 768) {
    sidebar.value.offScreen = true
  }
})

const onMinimize = () => {
  if (width.value <= 768) {
    sidebar.value.offScreen = true
  } else {
    sidebar.value.minimize = !sidebar.value.minimize
  }
}

const stopPlaying = () => {
  systemStore.stopPlaying()
  hash.value = ''
}

/**
 * @FIXME Posteriormente melhorar essa lógica para não chamar
 * useSecoesStore nesse componente de layout, mas sim no homeIndex
 */
const setLocalProgress = ({progress, id}: {id: string, progress: number}) => {
  useSecoesStore().updateMedia({itemId: id, progress})
}

onBeforeMount(() => {
  indicadorRespostaStore.getActiveIndicator()
})

onMounted(() => {
  try {
    const auth = useAuthStore()
    const showCommunication = webStorage.getItem('showCommunication')

    nextTick(() => {
      if (showCommunication && !auth.mustAcceptTermsOfUse && !auth.clientMode?.contract && useComunicacoesStore()?.getUserCommunication()) {
        useContratosStore()?.getIsDistribuidor()
      }

      systemStore.checkUserTermsAgreement()
      // customizationStore.loadExistingData()
    })
  } catch (error) {
    //
  }
})
</script>
